import { useState } from "react";
import { useTranslation } from "react-i18next";
import Joi from "joi";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { toast } from "react-toastify";
import BsInputPassword from "../../../public/BsInputPassword";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import LanguageSelector from "../../../atoms/LanguageSelector";
import SocialAuth from "../../lmcUserVIew/SocialAuth";
import { IResolveParams } from "reactjs-social-login";
import { Timer } from "../../../../utils/Timer";
const EMAIL_REQUIRED = "validation.emailRequired";
const EMAIL_INVALID = "validation.emailInvalid";
const PASSWORD_REQUIRED =
  "dashboard.features.loginTemplate.loginModal.passwordRequired";

const accountEmail = Joi.string()
  .trim()
  .email({ tlds: { allow: false } });

/** Its messages are clientView keys, translated where they are shown. */
const loginSchema = Joi.object({
  user_email: accountEmail.required().messages({
    "any.required": EMAIL_REQUIRED,
    "string.empty": EMAIL_REQUIRED,
    "string.email": EMAIL_INVALID,
  }),
  user_password: Joi.string().trim().required().messages({
    "any.required": PASSWORD_REQUIRED,
    "string.empty": PASSWORD_REQUIRED,
  }),
}).required();

const LoginModal = ({
  showModal,
  serviceData,
  isRealLogin,
  handleLoginViaLink,
  handleSocialLogin,
  handleForgotPassword,
  handlePassLessLogin,
  isForgotPasswordLoading,
  externalPage,
  showPreview,
}: {
  showModal: boolean;
  serviceData: { name: string; logoUrl: string };
  isRealLogin?: boolean;
  handleLoginViaLink?: (loginData: any) => void;
  handleSocialLogin?: (provider: string, data: any) => void;
  /** `onSent` once the link has left: the client waits for it, and may try again after a refusal. */
  handleForgotPassword?: (email: string, onSent: () => void) => void;
  /** With the address typed on the page, when it is a valid one, for the window to start from. */
  handlePassLessLogin?: (email: string) => void;
  isForgotPasswordLoading?: boolean;
  externalPage?: boolean;
  showPreview?: boolean;
}) => {
  const { t } = useTranslation("clientView");

  const [view, setView] = useState<"login" | "forgotPassword">("login");
  const [showTimer, setShowTimer] = useState(false);

  const showSocialUI = (isRealLogin && handleSocialLogin) || showPreview;
  const showForgotUI = (isRealLogin && handleForgotPassword) || showPreview;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm({
    resolver: joiResolver(loginSchema),
  });

  const {
    handleSubmit: handleForgotSubmit,
    formState: { errors: forgotErrors },
    control: forgotControl,
    getValues: getForgotValues,
  } = useForm();

  const onSubmit = () => {
    if (handleLoginViaLink && isRealLogin) {
      const data = getValues();
      handleLoginViaLink({ ...data });
    }
  };

  const onForgotSubmit = () => {
    if (handleForgotPassword) {
      const email = getForgotValues("forgot_email");
      handleForgotPassword(email, () => setShowTimer(true));
    }
  };

  return (
    <div
      className={`w-full ${
        externalPage ? "md:w-[792px]" : ""
      } mx-auto self-center px-6 pt-5 pb-6 bg-white rounded-lg shadow border border-gray-300 flex-col justify-start items-center gap-4 inline-flex relative ${
        !isRealLogin ? "pointer-events-none select-none" : ""
      }`}
    >
      <div className="absolute top-2 end-2 z-10">
        <LanguageSelector variant="compact" disabled={!isRealLogin} />
      </div>
      <img
        className="w-16 h-16 relative bg-gray-100 rounded-full"
        src={`${
          serviceData.logoUrl !== ""
            ? serviceData.logoUrl
            : "/images/login-modal-logo.svg"
        }`}
        alt=""
      />

      <h2 className="text-center text-gray-700 text-lg font-semibold font-figtree leading-7">
        {`${
          serviceData.name !== ""
            ? serviceData.name
            : t("dashboard.features.loginTemplate.loginModal.text3")
        }`}
      </h2>

      {view === "login" ? (
        <>
          <h3 className="text-center text-gray-700 text-lg font-semibold font-figtree leading-7">
            {t("dashboard.features.loginTemplate.loginModal.text2")}
          </h3>

          {showSocialUI && !showPreview && handleSocialLogin && (
            <SocialAuth
              className="w-full"
              providers={["Google", "Apple"]}
              onLoginStartSocialAuthStart={() => {}}
              onResolveSocialAuth={(
                provider: IResolveParams["provider"],
                data: IResolveParams["data"]
              ) => {
                handleSocialLogin(provider, data);
              }}
              onRejectSocialAuth={(err: any) => {
                if (err?.err?.error !== "popup_closed_by_user") {
                  toast.error(t("loginPage.errors.generic") as string, {
                    toastId: "loginPage.errors.generic",
                  });
                }
              }}
              providersIcon={{
                Google: (
                  <div className="relative w-full h-11 px-2.5 py-2 bg-white hover:bg-blue-400/5 rounded-md shadow border border-gray-300 flex justify-center items-center cursor-pointer">
                    <img
                      src="/images/google.svg"
                      alt=""
                      className="absolute left-2.5 top-2.5 w-6 h-6"
                    />
                    <p className="text-gray-700 text-sm font-medium font-inter leading-tight">
                      {t("login.socialGoogle")}
                    </p>
                  </div>
                ),
                Apple: (
                  <div className="relative w-full h-11 px-2.5 py-2 bg-white hover:bg-blue-400/5 rounded-md shadow border border-gray-300 flex justify-center items-center cursor-pointer">
                    <img
                      src="/images/apple-black.svg"
                      alt=""
                      className="absolute left-2.5 top-2.5 w-6 h-6"
                    />
                    <p className="text-gray-700 text-sm font-medium font-inter leading-tight">
                      {t("login.socialApple")}
                    </p>
                  </div>
                ),
              }}
              redirectUrl={{
                Apple: `${process.env.REACT_APP_APPLE_CALLBACK}`,
                Google: window.location.href,
              }}
            />
          )}

          {showPreview && (
            <div className="w-full flex flex-col gap-3">
              <div className="relative w-full h-11 px-2.5 py-2 bg-white rounded-md shadow border border-gray-300 flex justify-center items-center">
                <img
                  src="/images/google.svg"
                  alt=""
                  className="absolute left-2.5 top-2.5 w-6 h-6"
                />
                <p className="text-gray-700 text-sm font-medium font-inter leading-tight">
                  {t("login.socialGoogle")}
                </p>
              </div>
              <div className="relative w-full h-11 px-2.5 py-2 bg-white rounded-md shadow border border-gray-300 flex justify-center items-center">
                <img
                  src="/images/apple-black.svg"
                  alt=""
                  className="absolute left-2.5 top-2.5 w-6 h-6"
                />
                <p className="text-gray-700 text-sm font-medium font-inter leading-tight">
                  {t("login.socialApple")}
                </p>
              </div>
            </div>
          )}

          {showSocialUI && (
            <div className="w-full flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-300" />
              <span className="text-gray-500 text-sm font-medium">
                {t("login.or")}
              </span>
              <div className="flex-1 h-px bg-gray-300" />
            </div>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-full flex flex-col justify-start items-center gap-4"
            autoComplete="off"
          >
            <div className="w-full">
              <CustomInput
                name="user_email"
                label={
                  t(
                    "dashboard.features.loginTemplate.loginModal.text7"
                  ) as string
                }
                className="w-full"
                type="email"
                placeholder={`${t(
                  "dashboard.features.loginTemplate.loginModal.text8"
                )}`}
                grayBackground
                helperText={
                  errors.user_email?.message
                    ? (t(errors.user_email.message as string) as string)
                    : undefined
                }
                disabled={!showModal || !isRealLogin}
                readOnly={!isRealLogin}
                error={errors.user_email}
                control={control}
                autoComplete="email"
              />
            </div>

            <div className="w-full relative">
              <BsInputPassword
                rhfControls={register("user_password")}
                errorMessage={
                  errors.user_password?.message
                    ? (t(errors.user_password.message as string) as string)
                    : undefined
                }
                isInvalid={!!errors.user_password}
                label={t(
                  "dashboard.features.loginTemplate.loginModal.text5"
                ).toString()}
                placeholder={t(
                  "dashboard.features.loginTemplate.loginModal.text5"
                ).toString()}
                rest={{ readOnly: !isRealLogin, disabled: !isRealLogin }}
              />
              {showForgotUI && (
                <div className="flex justify-end mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      if (handleForgotPassword) {
                        setView("forgotPassword");
                      }
                    }}
                    className="text-blue-500 text-xs hover:text-blue-400 transition"
                  >
                    {t(
                      "dashboard.features.loginTemplate.loginModal.forgotPassword"
                    )}
                  </button>
                </div>
              )}
            </div>

            {isRealLogin ? (
              <>
                <CustomButton
                  type="submit"
                  variant="primary"
                  className={`w-full justify-center items-center inline-flex`}
                  title={t("login.loginBtn")}
                />
                {handlePassLessLogin && (
                  <CustomButton
                    variant="secondary"
                    className="w-full"
                    title={t("passwordless.loginBtn")}
                    onClick={() => {
                      const typed = String(
                        getValues("user_email") ?? ""
                      ).trim();
                      const { error } = loginSchema
                        .extract("user_email")
                        .validate(typed);
                      handlePassLessLogin(error ? "" : typed);
                    }}
                  />
                )}
              </>
            ) : (
              <>
                <div
                  className={`w-full px-4 py-2 rounded-md shadow justify-center items-center inline-flex ${
                    !showModal || !isRealLogin ? "bg-gray-400" : "bg-gray-700"
                  } ${
                    handleLoginViaLink ? "cursor-pointer hover:bg-gray-600" : ""
                  }`}
                >
                  <span className="text-white text-sm font-medium font-figtree leading-tight">
                    {t("login.loginBtn")}
                  </span>
                </div>
                <CustomButton
                  variant="secondary"
                  className="w-full"
                  title={t("passwordless.loginBtn")}
                  disabled
                />
              </>
            )}
          </form>
        </>
      ) : (
        <>
          <h3 className="text-center text-gray-700 text-lg font-semibold font-figtree leading-7">
            {t(
              "dashboard.features.loginTemplate.loginModal.forgotPasswordTitle"
            )}
          </h3>

          <p className="text-center text-gray-500 text-sm font-normal leading-5">
            {t(
              "dashboard.features.loginTemplate.loginModal.forgotPasswordNote"
            )}
          </p>

          <form
            onSubmit={handleForgotSubmit(onForgotSubmit)}
            className="w-full flex flex-col justify-start items-center gap-4"
            autoComplete="off"
          >
            <div className="w-full">
              <CustomInput
                name="forgot_email"
                control={forgotControl}
                error={forgotErrors.forgot_email}
                helperText={
                  forgotErrors.forgot_email?.type === "required"
                    ? (t(EMAIL_REQUIRED) as string)
                    : forgotErrors.forgot_email?.type === "validate"
                    ? (t(EMAIL_INVALID) as string)
                    : ""
                }
                grayBackground
                type="email"
                className="w-full"
                label={
                  t(
                    "dashboard.features.loginTemplate.loginModal.forgotPasswordEmail"
                  ) as string
                }
                placeholder={
                  t(
                    "dashboard.features.loginTemplate.loginModal.forgotPasswordEmailPlaceholder"
                  ) as string
                }
                rules={{
                  required: true,
                  validate: (value: string) =>
                    !accountEmail.validate(value).error ||
                    (t(EMAIL_INVALID) as string),
                }}
                autoComplete="email"
              />
            </div>

            {showTimer ? (
              <p className="text-center text-gray-500 text-sm">
                {t(
                  "dashboard.features.loginTemplate.loginModal.forgotPasswordWait"
                )}{" "}
                <span className="font-medium text-gray-700">
                  <Timer setShowTimer={setShowTimer} ttl={120} />
                </span>
              </p>
            ) : (
              <CustomButton
                type="submit"
                variant="primary"
                className="w-full justify-center items-center inline-flex"
                isLoading={isForgotPasswordLoading}
                title={
                  t(
                    "dashboard.features.loginTemplate.loginModal.forgotPasswordSendBtn"
                  ) as string
                }
              />
            )}

            <button
              type="button"
              onClick={() => {
                setView("login");
                setShowTimer(false);
              }}
              className="text-blue-500 text-sm hover:text-blue-400 transition"
            >
              {t(
                "dashboard.features.loginTemplate.loginModal.forgotPasswordBackBtn"
              )}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default LoginModal;
